import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type EventsFeatureContextValue = {
  /** False until the first successful fetch to `/api/feature-flags` completes. */
  ready: boolean;
  eventsEnabled: boolean;
  /** Matches server `isEventEligibilityV2Enabled()` (tier vs in-person publish, etc.). */
  eventEligibilityV2Enabled: boolean;
  /** Vercel Flags key `forecast` — seasonal outlook hub + upland pages. */
  forecastEnabled: boolean;
};

const EventsFeatureContext = createContext<EventsFeatureContextValue>({
  ready: false,
  eventsEnabled: false,
  eventEligibilityV2Enabled: false,
  forecastEnabled: false,
});

export function EventsFeatureProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [ready, setReady] = useState(false);
  const [eventsEnabled, setEventsEnabled] = useState(false);
  const [eventEligibilityV2Enabled, setEventEligibilityV2Enabled] =
    useState(false);
  const [forecastEnabled, setForecastEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/feature-flags", {
          credentials: "same-origin",
        });
        const data = (await response.json().catch(() => ({}))) as {
          events?: boolean;
          eventEligibilityV2?: boolean;
          forecast?: boolean;
        };
        if (!cancelled) {
          setEventsEnabled(Boolean(data?.events));
          setEventEligibilityV2Enabled(Boolean(data?.eventEligibilityV2));
          setForecastEnabled(Boolean(data?.forecast));
        }
      } catch {
        if (!cancelled) {
          setEventsEnabled(false);
          setEventEligibilityV2Enabled(false);
          setForecastEnabled(false);
        }
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      ready,
      eventsEnabled,
      eventEligibilityV2Enabled,
      forecastEnabled,
    }),
    [ready, eventsEnabled, eventEligibilityV2Enabled, forecastEnabled],
  );

  return (
    <EventsFeatureContext.Provider value={value}>
      {children}
    </EventsFeatureContext.Provider>
  );
}

export function useEventsFeature(): EventsFeatureContextValue {
  return useContext(EventsFeatureContext);
}
